import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { Section, SectionHeader, Breadcrumbs, CTABand, FAQ } from "@/components/site/Blocks";
import { RatePricingDisclosure } from "@/components/rates/RateDisclosures";
import { RateRefreshSchedule } from "@/components/rates/RateRefreshSchedule";
import { getDscrRateHistory } from "@/lib/rate-history.functions";
import {
  HISTORY_WINDOWS,
  formatChange,
  formatRate,
  sparklinePoints,
  type HistoryWindow,
  type RateHistorySeries,
} from "@/lib/rate-history";
import { buildHead } from "@/lib/seo-head";
import { REFRESH_SCHEDULE_LABEL } from "@/lib/rate-refresh-schedule";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";
import heroRateHistory from "@/assets/hero/hero-dscr-rate-history-desk-trend.jpg";

const URL = "/dscr-rate-history";

const KEYWORDS = [
  "DSCR rate history",
  "DSCR loan rate trends",
  "investment property rate history",
  "rental property mortgage rate trend",
  "DSCR purchase rate history",
  "DSCR cash-out refinance rates history",
  "DSCR rate and term refinance rates",
  "30 day DSCR rate trend",
  "90 day DSCR rate trend",
  "DSCR rate changes",
  "investor loan rate tracker",
  "DSCR pricing updates",
].join(", ");

const FAQS = [
  {
    q: "How often is DSCR pricing refreshed?",
    a: `Canonical DSCR scenarios are re-priced on a schedule — ${REFRESH_SCHEDULE_LABEL} — and each published result is logged here. Pricing can move between scheduled refreshes.`,
  },
  {
    q: "What scenario does this history track?",
    a: "One canonical sample scenario per loan purpose (purchase, cash-out refinance, rate-and-term refinance). It is a trend reference only, not a quote for your property.",
  },
  {
    q: "Why do some days show no data point?",
    a: "Only real, successful pricing responses are logged. If a scheduled refresh returned no eligible pricing, nothing is recorded for that run rather than an estimated value.",
  },
  {
    q: "Can I use this history to lock a rate?",
    a: "No. Historical figures are illustrative and are not an offer of credit or a commitment to lend. Final pricing depends on a complete scenario, credit and property review, and full underwriting.",
  },
  {
    q: "Does the history include HELOC or second-lien pricing?",
    a: "No. This tracker logs canonical DSCR first-lien scenarios only. Second-lien and HELOC pricing move on their own schedule and are shown on their dedicated pricing tool page instead.",
  },
  {
    q: "Why does pricing move day to day even without a rate announcement?",
    a: "Investor and secondary-market pricing for non-owner-occupied loans adjusts continuously based on capital markets conditions, not on a public rate-announcement calendar. The logged value simply reflects whatever the active pricing schedule returned at that scheduled run.",
  },
  {
    q: "Is the sample scenario representative of what I would qualify for?",
    a: "Only loosely. The canonical scenario fixes FICO, DSCR, and leverage at a single reference point so the trend is comparable over time, but your own credit tier, DSCR, and loan amount will move your actual pricing up or down.",
  },
  {
    q: "Can I download or export this pricing history?",
    a: "The page is intended as a visual trend reference rather than a data export tool. If you need historical figures for a specific scenario or date range, contact a loan officer directly.",
  },
  {
    q: "How far back does the DSCR rate history go?",
    a: "The chart displays as much logged history as has accumulated since tracking began and will keep extending over time. Earlier gaps may appear where a scheduled run returned no eligible pricing.",
  },
  {
    q: "Does a rate movement in this history mean my locked rate would change?",
    a: "No. This history only reflects the reference scenario logged on each date. A rate you have already locked with a submitted application is unaffected by later movements shown here.",
  },
  {
    q: "What loan purposes appear in the DSCR rate history chart?",
    a: "Purchase, cash-out refinance, and rate-and-term refinance each have their own canonical scenario tracked and logged separately. Use the toggle on the chart to view each purpose's individual trend line.",
  },
  {
    q: "Why might my quoted rate differ from the historical figure shown for the same day?",
    a: "The logged figure reflects one fixed reference scenario at a set FICO, DSCR, and leverage combination, while your actual quote reflects your specific credit tier, property, and loan amount. Two different scenarios priced the same day can land at different rates.",
  },
];

function dayLabel(iso: string): string {
  const d = new Date(iso);
  return Number.isFinite(d.getTime())
    ? d.toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : "—";
}

function timeLabel(iso: string): string {
  const d = new Date(iso);
  return Number.isFinite(d.getTime())
    ? d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
    : "—";
}

function SeriesCard({ s }: { s: RateHistorySeries }) {
  const path = sparklinePoints(s.points);
  const recent = [...s.points].reverse().slice(0, 8);
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-base font-bold text-brand-dark">{s.label}</h3>
        <span className="text-xs text-muted-foreground">{s.points.length} published refreshes</span>
      </div>

      {path ? (
        <svg
          role="img"
          aria-label={`${s.label} lowest published rate trend`}
          viewBox="0 0 240 48"
          className="mt-4 h-14 w-full"
          preserveAspectRatio="none"
        >
          <polyline points={path} fill="none" stroke="currentColor" strokeWidth="2" className="text-brand" />
        </svg>
      ) : (
        <p className="mt-4 text-sm text-muted-foreground">
          Not enough published refreshes in this window to draw a trend yet.
        </p>
      )}

      <dl className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
        <div>
          <dt className="text-xs text-muted-foreground">Latest low</dt>
          <dd className="font-bold text-brand-dark">{formatRate(s.latest?.lowRate ?? null)}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Window change</dt>
          <dd className="font-bold text-brand-dark">{formatChange(s.changeRate)}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Window low</dt>
          <dd className="font-semibold">{formatRate(s.low)}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Window high</dt>
          <dd className="font-semibold">{formatRate(s.high)}</dd>
        </div>
      </dl>

      {recent.length ? (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="text-muted-foreground">
              <tr>
                <th scope="col" className="py-1 pr-3 font-semibold">Refresh</th>
                <th scope="col" className="py-1 pr-3 font-semibold">Lowest rate</th>
                <th scope="col" className="py-1 pr-3 font-semibold">Par rate</th>
                <th scope="col" className="py-1 font-semibold">Options</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((p) => (
                <tr key={p.asOf} className="border-t border-border/60">
                  <td className="py-1.5 pr-3">{timeLabel(p.asOf)}</td>
                  <td className="py-1.5 pr-3 font-semibold">{formatRate(p.lowRate)}</td>
                  <td className="py-1.5 pr-3">{formatRate(p.parRate)}</td>
                  <td className="py-1.5">{p.optionCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {s.first && s.latest ? (
        <p className="mt-3 text-[11px] text-muted-foreground">
          Window starts {dayLabel(s.first.asOf)} and ends {dayLabel(s.latest.asOf)}.
        </p>
      ) : null}
    </div>
  );
}

function HistoryView() {
  const [days, setDays] = useState<HistoryWindow>(30);
  const fetchHistory = useServerFn(getDscrRateHistory);
  const { data, isPending } = useQuery({
    queryKey: ["dscrRateHistory", days],
    queryFn: () => fetchHistory({ data: { days } }),
    staleTime: 15 * 60 * 1000,
  });

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        {HISTORY_WINDOWS.map((w) => (
          <button
            key={w}
            type="button"
            onClick={() => setDays(w)}
            aria-pressed={days === w}
            className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition ${
              days === w
                ? "border-brand bg-brand text-brand-foreground"
                : "border-border bg-card text-foreground/80 hover:border-brand"
            }`}
          >
            Last {w} days
          </button>
        ))}
      </div>

      <RateRefreshSchedule className="mt-4" showHistoryLink={false} />

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        {isPending ? (
          <div className="rounded-2xl border border-dashed border-border bg-card p-6 text-sm text-muted-foreground">
            Loading published rate history…
          </div>
        ) : (
          (data?.series ?? []).map((s) => <SeriesCard key={s.loanPurpose} s={s} />)
        )}
      </div>

      <RatePricingDisclosure className="mt-6" />
    </div>
  );
}

export const Route = createFileRoute("/dscr-rate-history")({
  head: () =>
    buildHead({
      title: "DSCR Rate History — 30 & 90 Day Trends by Loan Purpose",
      description:
        "Track published DSCR pricing over the last 30 or 90 days for purchase, cash-out refinance, and rate-and-term refinance — with the exact refresh schedule behind every update.",
      url: URL,
      keywords: KEYWORDS,
    }),
  component: RateHistoryPage,
});

function RateHistoryPage() {
  return (
    <SiteShell>
      <PageHero
        eyebrow="Rate center"
        title="DSCR Rate History"
        subtitle="See how published DSCR pricing has moved across the last 30 and 90 days for purchase, cash-out refinance, and rate-and-term refinance scenarios."
        image={{ src: heroRateHistory, alt: "Office desk with a monitor showing DSCR rate trend lines over time and a small model house" }}
      />
      <PageEssentials />
      <Section>
        <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "DSCR rate history" }]} />
        <SectionHeader
          eyebrow="Published refreshes"
          title="Every scheduled DSCR pricing refresh, logged"
        />
        <p className="mt-4 max-w-3xl text-sm leading-relaxed text-muted-foreground">
          Each point below is a real published pricing result for one canonical sample scenario per loan
          purpose — nothing is estimated or back-filled. Use it to see direction of travel, then price your
          own property for figures that reflect your DSCR, LTV, credit tier, and state.
        </p>
        <div className="mt-8">
          <HistoryView />
        </div>
      </Section>
      <Section>
        <FAQ items={FAQS} />
      </Section>
      <CTABand
        title="Want pricing for your actual property?"
        body="A licensed loan officer reviews your scenario and follows up, typically the same business day."
      />
    <PageOutro />
      </SiteShell>
  );
}
